'use client';

import ReactPlayer from 'react-player/lazy';
import { cn } from '@/app/utils/cn';
import { useRef, useState, useEffect, useCallback, useId } from 'react';
import { IconPlayerPauseFilled, IconPlayerPlayFilled } from '@tabler/icons-react';
import { encodeAssetUrl } from '@/app/utils/stringUtils';
import Image from 'next/image';

function Video({
    imagePrimaryLoad,
    title,
    thumbnailAlt,
    thumbnail,
    identifier,
    muted = false,
    onDuration,
    vtt,
    paragraphSpacing = 'nospace',
    videoClassName
}) {
    const speeds = [1, 1.5, 2];
    const playerRef = useRef(null);
    const videoContainerRef = useRef(null);

    const [isMuted, setIsMuted] = useState(muted);
    const [progress, setProgress] = useState(0);
    const [volume, setVolume] = useState(0.8);
    const [videoPlaying, setVideoPlaying] = useState(false);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [isLooping, setIsLooping] = useState(false);
    const [showSubtitles, setShowSubtitles] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [videoUrl, setVideoUrl] = useState('');
    const [playedSeconds, setPlayedSeconds] = useState(0);
    const [isClient, setIsClient] = useState(false);

    const config = {
        file: {
            forceReload: true,
            attributes: {
                crossOrigin: 'anonymous'
            },
            tracks: vtt
                ? [
                      {
                          kind: 'subtitles',
                          src: `/dA/${vtt}`,
                          srcLang: 'en',
                          default: showSubtitles
                      }
                  ]
                : []
        }
    };

    useEffect(() => {
        setIsClient(true);
    }, []);

    useEffect(() => {
        if (identifier) {
            const url = `/dA/${identifier}`;
            setVideoUrl(url);
        }
    }, [identifier]);

    const handleVideoDuration = (duration) => {
        const minutes = Math.floor(duration / 60);
        const seconds = Math.floor(duration % 60);
        const formattedDuration = `${minutes}m ${seconds}s`;
        onDuration && onDuration(formattedDuration);
    };

    const handleSpeedChange = (speed) => {
        setPlaybackRate(speed);
    };

    const toggleFullscreen = () => {
        if (videoContainerRef.current) {
            if (!document.fullscreenElement) {
                videoContainerRef.current.requestFullscreen().then(() => setIsFullscreen(true));
            } else {
                document.exitFullscreen().then(() => setIsFullscreen(false));
            }
        }
    };

    const handleSeek = (event) => {
        if (playerRef.current) {
            const rect = event.currentTarget.getBoundingClientRect();
            const clickX = event.clientX - rect.left;
            const newProgress = clickX / rect.width;
            playerRef.current.seekTo(newProgress, 'fraction');
        }
    };

    const toggleMute = useCallback(() => setIsMuted((prev) => !prev), []);
    const toggleLoop = useCallback(() => setIsLooping((prev) => !prev), []);
    const toggleSubtitles = useCallback(() => setShowSubtitles((prev) => !prev), []);

    const togglePlayPause = () => {
        setVideoPlaying((prev) => {
            if (prev && playerRef.current) {
                setPlayedSeconds(playerRef.current.getCurrentTime());
            } else if (playerRef.current) {
                playerRef.current.seekTo(playedSeconds, 'seconds');
            }
            return !prev;
        });
    };

    const onPause = () => {
        if (playerRef.current) {
            const currentTime = playerRef.current.getCurrentTime();
            setPlayedSeconds(currentTime);
        }
    };

    const handleVolumeChange = (e) => {
        const newVolume = parseFloat(e.target.value);
        setVolume(newVolume);
    };

    const playerKey = `${videoUrl}-${showSubtitles}`;
    const uniqueId = useId();
    const shouldShowThumbnail =
        (thumbnail || identifier) && thumbnail !== 'https://System Host' && !videoPlaying;
    const thumbnailUrl = encodeAssetUrl(thumbnail || `/dA/${identifier}/thumbnail`);

    return (
        <div className={cn(paragraphSpacing !== 'nospace' && `spacing-${paragraphSpacing}`)}>
            <div
                className={cn(
                    'relative h-[32rem] w-full overflow-hidden rounded-xl bg-black md:h-[46rem]',
                    videoClassName
                )}
                ref={videoContainerRef}>
                {/* Click-to-play area */}
                <button
                    className="absolute inset-0 size-full cursor-pointer"
                    onClick={togglePlayPause}
                    aria-label={videoPlaying ? 'Pause video' : 'Play video'}>
                    <div className="size-full overflow-hidden">
                        <div className="relative h-full transition-all duration-700">
                            {shouldShowThumbnail && (
                                <Image
                                    src={thumbnailUrl}
                                    width={0}
                                    height={0}
                                    sizes="(max-width: 800px) calc(100vw - 32px), 1280px"
                                    quality={75}
                                    priority={!!imagePrimaryLoad}
                                    className={cn(
                                        `absolute size-full object-cover transition-all duration-700 ${
                                            videoPlaying ? 'opacity-0' : 'opacity-1'
                                        }`
                                    )}
                                    alt={thumbnailAlt || title || 'video thumbnail'}
                                />
                            )}
                            {isClient && (
                                <ReactPlayer
                                    key={playerKey}
                                    ref={playerRef}
                                    onPause={onPause}
                                    playing={videoPlaying}
                                    muted={isMuted}
                                    loop={isLooping}
                                    volume={volume}
                                    url={videoUrl}
                                    width="100%"
                                    height="100%"
                                    config={config}
                                    playbackRate={playbackRate}
                                    style={{
                                        top: 0,
                                        left: 0,
                                        objectFit: 'cover',
                                        pointerEvents: 'none'
                                    }}
                                    onProgress={({ played, playedSeconds }) => {
                                        setProgress(played);
                                        setPlayedSeconds(playedSeconds || 0);
                                    }}
                                    onDuration={handleVideoDuration}
                                    onEnded={() => {
                                        setVideoPlaying(false);
                                        setPlayedSeconds(0);
                                        setProgress(0);
                                    }}
                                    onError={(e) => console.error('ReactPlayer error:', e)}
                                    progressInterval={60}
                                />
                            )}
                        </div>
                    </div>
                </button>

                {/* Pill controls overlay */}
                <div className="absolute inset-x-4 bottom-4 z-10 flex items-center gap-2 rounded-full bg-black/70 px-3 py-2.5 backdrop-blur-md">
                    {/* Play/Pause */}
                    <button
                        aria-label={videoPlaying ? 'Pause' : 'Play'}
                        onClick={togglePlayPause}
                        className="flex size-8 shrink-0 items-center justify-center rounded-full bg-white">
                        {videoPlaying ? (
                            <IconPlayerPauseFilled className="size-3.5 text-fuschia" />
                        ) : (
                            <IconPlayerPlayFilled className="size-3.5 text-fuschia" />
                        )}
                    </button>

                    {/* Progress bar */}
                    <div
                        className="relative h-1 flex-1 cursor-pointer rounded-full"
                        onClick={handleSeek}>
                        <div className="absolute inset-0 rounded-full bg-white/30" />
                        <div
                            className="absolute inset-y-0 left-0 rounded-full bg-white"
                            style={{ width: `${progress * 100}%` }}
                        />
                    </div>

                    {/* Mute */}
                    <button aria-label="mute" onClick={toggleMute} className="shrink-0">
                        <Image
                            src={
                                isMuted
                                    ? '/assets/icons/video/video-mute-on.svg'
                                    : '/assets/icons/video/video-mute-off.svg'
                            }
                            width={18}
                            height={18}
                            className="invert"
                            alt="toggle mute"
                            unoptimized
                        />
                    </button>

                    {/* Volume slider — hidden on mobile */}
                    <div className="hidden items-center sm:flex">
                        <label htmlFor={`volume-range-${uniqueId}`} className="sr-only">
                            Volume
                        </label>
                        <input
                            id={`volume-range-${uniqueId}`}
                            type="range"
                            min="0"
                            max="1"
                            step="0.01"
                            value={volume}
                            onChange={handleVolumeChange}
                            className="w-16 cursor-pointer appearance-none rounded-lg"
                            style={{
                                background: `linear-gradient(to right, white ${volume * 100}%, rgba(255,255,255,0.3) ${volume * 100}%)`
                            }}
                        />
                    </div>

                    {/* Speed — hidden on mobile */}
                    <div className="hidden items-center md:flex">
                        {speeds.map((speed) => (
                            <button
                                key={`speed_${speed}`}
                                className={cn(
                                    'rounded px-1 text-xs text-white/50',
                                    playbackRate === speed && 'font-bold text-white'
                                )}
                                onClick={() => handleSpeedChange(speed)}>
                                {speed}x
                            </button>
                        ))}
                    </div>

                    {/* Loop */}
                    <button aria-label="loop" onClick={toggleLoop} className="shrink-0">
                        <Image
                            src={
                                isLooping
                                    ? '/assets/icons/video/video-loop-on.svg'
                                    : '/assets/icons/video/video-loop-off.svg'
                            }
                            width={18}
                            height={18}
                            className="invert"
                            alt="toggle loop"
                            unoptimized
                        />
                    </button>

                    {/* Subtitles */}
                    {vtt && (
                        <button
                            aria-label="subtitles"
                            onClick={toggleSubtitles}
                            className="shrink-0">
                            <Image
                                src={
                                    showSubtitles
                                        ? '/assets/icons/video/video-subtitles-on.svg'
                                        : '/assets/icons/video/video-subtitles-off.svg'
                                }
                                width={18}
                                height={18}
                                className="invert"
                                alt="toggle subtitles"
                                unoptimized
                            />
                        </button>
                    )}

                    {/* Fullscreen */}
                    <button
                        aria-label="full screen"
                        onClick={toggleFullscreen}
                        className="shrink-0">
                        <Image
                            src={
                                isFullscreen
                                    ? '/assets/icons/video/video-toggle-screen-on.svg'
                                    : '/assets/icons/video/video-toggle-screen-off.svg'
                            }
                            width={18}
                            height={18}
                            className="invert"
                            alt="toggle fullscreen"
                            unoptimized
                        />
                    </button>
                </div>
            </div>
        </div>
    );
}

export default Video;
